/**
 * ControlPanel API (`GET /language-health`) クライアント (SBI-17)。
 *
 * 「特定言語だけ音声が生成されない」という言語別のサイレント欠落を、管理画面で
 * 確認するための読み取り専用 API。ContentsTable の `contents_schedule[]` を
 * 言語別に集計した結果を返す。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 *
 * ⚠️ `languageCode` に渡す値は Polly の `LanguageCode` (`ja-JP`) ではなく
 *    **language_key** (`ja` / `en` / `zh` / `ko`) を使うこと。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './common';
import type { ApiErrorBody } from './types';

/** 1 言語ぶんの生成状況。単位は `contents_schedule[]` の要素 (entry)。 */
export interface LanguageHealthStats {
  /** その言語の `contents_schedule[]` 要素数 */
  entry_count: number;
  /** その言語の要素を 1 つ以上持つアイテム数 */
  item_count: number;
  /** `message` が無い / 空白のみの要素数 */
  empty_message_count: number;
  /** `audio_key` が空 / 欠落の要素数。0 より大きければ、その言語の音声が生成されていない */
  missing_audio_key_count: number;
}

/** `GET /language-health` の応答。 */
export interface LanguageHealthResponse {
  /** 生成時刻 (RFC 3339 / UTC・`Z` 終端) */
  generated_at: string;
  filter: { language_code: string | null };
  /** 走査したアイテム数 */
  scanned_item_count: number;
  /** 走査上限で打ち切ったか。`true` なら不完全な集計 */
  truncated: boolean;
  /** 言語キーごとの生成状況 */
  by_language: Record<string, LanguageHealthStats>;
}

export interface LanguageHealthClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class LanguageHealthClient {
  private readonly options: LanguageHealthClientOptions;

  constructor(options: LanguageHealthClientOptions) {
    this.options = options;
  }

  /**
   * GET /language-health
   *
   * @param languageCode 指定すると `by_language` をその言語だけに絞る (省略時は全言語)。
   *   前後の空白は除去し、空白のみは未指定として扱う。
   */
  async get(languageCode?: string): Promise<LanguageHealthResponse> {
    const params = new URLSearchParams();
    if (typeof languageCode === 'string' && languageCode.trim() !== '') {
      params.set('language_code', languageCode.trim());
    }
    const query = params.size > 0 ? `?${params.toString()}` : '';
    const url = joinUrl(this.options.config.apiUrl, `language-health${query}`);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? `API request failed: HTTP ${res.status}`;
      throw new ApiError(res.status, message, errorBody);
    }

    return (await res.json()) as LanguageHealthResponse;
  }
}
